/**
 * Resolves metaobject GIDs rendered by the Equate comparison app
 * into human-readable values using the Storefront API,
 * then restructures specification rows into aligned, collapsible accordions.
 */
(function () {
  const GID_PATTERN = /gid:\/\/shopify\/Metaobject\/\d+/g;
  const GID_TEST = /gid:\/\/shopify\/Metaobject\/\d+/;
  const STOREFRONT_API_URL = window.__STOREFRONT_API_URL__;
  const STOREFRONT_TOKEN = window.__STOREFRONT_ACCESS_TOKEN__;

  if (!STOREFRONT_API_URL || !STOREFRONT_TOKEN) return;

  const cache = new Map();
  let debounceTimer = null;
  let isProcessing = false;

  function extractTextFromRichText(richTextJson) {
    try {
      const root = typeof richTextJson === 'string' ? JSON.parse(richTextJson) : richTextJson;
      const texts = [];
      function walk(node) {
        if (node.value) texts.push(node.value);
        if (node.children) node.children.forEach(walk);
      }
      walk(root);
      return texts.join(' ');
    } catch {
      return richTextJson || '';
    }
  }

  async function fetchMetaobjects(gids) {
    const uncached = gids.filter((id) => !cache.has(id));
    if (uncached.length === 0) return;

    for (let i = 0; i < uncached.length; i += 250) {
      const batch = uncached.slice(i, i + 250);
      const ids = JSON.stringify(batch);
      const query = `{ nodes(ids: ${ids}) { ... on Metaobject { id fields { key value type } } } }`;

      try {
        const res = await fetch(STOREFRONT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
          },
          body: JSON.stringify({ query }),
        });

        const json = await res.json();
        if (!json.data || !json.data.nodes) continue;

        for (const node of json.data.nodes) {
          if (!node || !node.id) continue;
          let type = '';
          let description = '';
          for (const field of node.fields) {
            if (field.key === 'type') type = field.value || '';
            if (field.key === 'description') description = extractTextFromRichText(field.value);
          }
          cache.set(node.id, { type, description });
        }
      } catch (err) {
        console.warn('[compare-resolver] Storefront API error:', err);
      }
    }
  }

  function formatMetaobjects(gids) {
    const lines = [];
    for (const gid of gids) {
      const data = cache.get(gid);
      if (data) {
        lines.push(data.type ? `${data.type}: ${data.description}` : data.description);
      }
    }
    return lines;
  }

  /**
   * Parse a "Type: Description" string into { label, value }.
   */
  function parseSpecLine(text) {
    var idx = text.indexOf(': ');
    if (idx > 0) {
      return { label: text.substring(0, idx), value: text.substring(idx + 2) };
    }
    return { label: '', value: text };
  }

  /**
   * Build the chevron SVG used in accordion headers.
   */
  function chevronSvg() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.classList.add('spec-accordion__chevron');
    var polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    polyline.setAttribute('points', '6 9 12 15 18 9');
    svg.appendChild(polyline);
    return svg;
  }

  /**
   * Read widths from an existing Equate row and set CSS variables
   * so the accordion spec rows match column widths.
   */
  function syncColumnWidth() {
    var existingRow = document.querySelector(
      '.sp-comp-t-row.sp-comp-t-row-meta:not(.sp-group-title):not([data-metaobject-restructured])'
    );
    if (!existingRow) return;
    var titleEl = existingRow.querySelector('.sp-comp-t-row-title');
    if (!titleEl) return;
    // Title cell has a max-width (typically 200px) that differs from product cells
    var titleWidth = Math.max(titleEl.getBoundingClientRect().width, 200);
    var titlePaddingL = parseFloat(getComputedStyle(titleEl).paddingLeft) || 0;
    var titlePaddingR = parseFloat(getComputedStyle(titleEl).paddingRight) || 0;
    document.documentElement.style.setProperty('--equate-title-width', titleWidth + 'px');
    document.documentElement.style.setProperty('--equate-title-padding', titlePaddingL + 'px ' + titlePaddingR + 'px');
    // Product cells use flex-basis percentage
    var productCell = existingRow.children[1];
    if (productCell) {
      var flexBasis = getComputedStyle(productCell).flexBasis;
      if (flexBasis && flexBasis !== 'auto') {
        document.documentElement.style.setProperty('--equate-col-width', flexBasis);
      }
      var cellPaddingL = parseFloat(getComputedStyle(productCell).paddingLeft) || 0;
      var cellPaddingR = parseFloat(getComputedStyle(productCell).paddingRight) || 0;
      document.documentElement.style.setProperty('--equate-cell-padding', cellPaddingL + 'px ' + cellPaddingR + 'px');
    }
  }

  /**
   * After all metaobject GIDs are resolved into "Type: Desc" spans,
   * restructure each .sp-comp-t-row into a collapsible accordion
   * with individual spec sub-rows aligned across product columns.
   */
  function restructureRows() {
    var rows = document.querySelectorAll('.sp-comp-t-row:not([data-metaobject-restructured])');

    for (var r = 0; r < rows.length; r++) {
      var row = rows[r];
      var resolvedCells = row.querySelectorAll('[data-metaobject-resolved]');
      if (resolvedCells.length === 0) continue;

      // Extract section title
      var titleEl = row.querySelector('.sp-comp-t-row-title p');
      var sectionTitle = titleEl ? titleEl.textContent.trim() : '';
      // Strip "Specification - " prefix
      sectionTitle = sectionTitle.replace(/^Specification\s*[-–—]\s*/i, '');

      var productCount = resolvedCells.length;

      // Parse specs from each product cell: array of arrays of {label, value}
      var productSpecs = [];
      var allLabels = []; // ordered unique labels
      var labelSet = {};

      for (var c = 0; c < resolvedCells.length; c++) {
        var spans = resolvedCells[c].querySelectorAll('span');
        var specs = [];
        for (var s = 0; s < spans.length; s++) {
          var parsed = parseSpecLine(spans[s].textContent.trim());
          specs.push(parsed);
          if (parsed.label && !labelSet[parsed.label]) {
            labelSet[parsed.label] = true;
            allLabels.push(parsed.label);
          }
        }
        productSpecs.push(specs);
      }

      // If no labels were found (all specs have no ": "), skip restructuring
      if (allLabels.length === 0) continue;

      // Build a lookup: for each product, map label -> value
      var productMaps = [];
      for (var p = 0; p < productSpecs.length; p++) {
        var map = {};
        for (var j = 0; j < productSpecs[p].length; j++) {
          var spec = productSpecs[p][j];
          if (spec.label) {
            map[spec.label] = spec.value;
          }
        }
        productMaps.push(map);
      }

      // Build accordion DOM
      var details = document.createElement('details');
      details.className = 'spec-accordion';
      details.setAttribute('open', '');

      var summary = document.createElement('summary');
      summary.className = 'spec-accordion__header';
      var titleSpan = document.createElement('span');
      titleSpan.textContent = sectionTitle;
      summary.appendChild(titleSpan);
      summary.appendChild(chevronSvg());
      details.appendChild(summary);

      var body = document.createElement('div');
      body.className = 'spec-accordion__body';
      body.style.setProperty('--product-count', String(productCount));

      for (var i = 0; i < allLabels.length; i++) {
        var label = allLabels[i];
        var subRow = document.createElement('div');
        subRow.className = 'spec-sub-row';

        var labelDiv = document.createElement('div');
        labelDiv.className = 'spec-sub-row__label';
        labelDiv.textContent = label;
        subRow.appendChild(labelDiv);

        // Collect values to compare across products
        var valuesForRow = [];
        for (var k = 0; k < productMaps.length; k++) {
          var val = productMaps[k][label] || '—';
          valuesForRow.push(val);
          var valDiv = document.createElement('div');
          valDiv.className = 'spec-sub-row__value';
          valDiv.textContent = val;
          subRow.appendChild(valDiv);
        }

        // Mark sub-row as matching or differing for per-row highlighting
        var allSame = valuesForRow.every(function (v) { return v === valuesForRow[0]; });
        if (!allSame) {
          subRow.classList.add('spec-sub-row--diff');
        }

        body.appendChild(subRow);
      }

      details.appendChild(body);

      // Replace row contents: hide original title + cells, insert accordion
      var titleWrapper = titleEl ? titleEl.closest('.sp-comp-t-row-title') : null;
      if (titleWrapper) titleWrapper.style.display = 'none';
      for (var d = 0; d < resolvedCells.length; d++) {
        /** @type {HTMLElement} */ (resolvedCells[d]).style.display = 'none';
      }

      row.appendChild(details);
      row.dataset.metaobjectRestructured = 'true';
    }
  }

  /**
   * Hide the native text elements below product images 
   * (Title, Variant, Price)
   */
  function hideTopProductInfo() {
    var headerBoxes = document.querySelectorAll('.sp-comp-t-row.sp-comp-t-row-head .sp-comp-t-box');
    for (var i = 0; i < headerBoxes.length; i++) {
        var box = headerBoxes[i];
        
        // Let's rely on CSS to hide them, we just add a class to the box
        box.classList.add('top-info-hidden');

        // Dynamically hide the price element via JS since its specific class might vary
        // The price comes after color/size and typically contains CAD, USD, $, IDR, etc.
        var textEls = box.querySelectorAll('p, span, div');
        for (var t = 0; t < textEls.length; t++) {
           var child = textEls[t];
           var txt = child.textContent || '';
           // Match common currencies and digits
           if (child.children.length <= 1 && txt.match(/(CAD|IDR|Rp|\$|€|£)/i) && /\d/.test(txt)) {
              child.style.display = 'none';
           }
        }
    }
  }

  /**
   * Build the "Overview" Accordion
   */
  async function buildOverviewAccordion() {
    // Check if already created
    if (document.querySelector('.spec-overview-accordion')) return;

    var headerRow = document.querySelector('.sp-comp-t-row.sp-comp-t-row-head');
    var headerBoxes = document.querySelectorAll('.sp-comp-t-row.sp-comp-t-row-head .sp-comp-t-box');
    if (!headerRow || headerBoxes.length <= 1) return; // Need at least the title box + 1 product

    // 1. Gather handles
    var products = [];
    for (var c = 0; c < headerBoxes.length; c++) {
      var origBox = headerBoxes[c];
      var links = origBox.querySelectorAll('a');
      var handle = '';
      for (var a = 0; a < links.length; a++) {
        if (links[a].href && links[a].href.indexOf('/products/') !== -1) {
          handle = links[a].href.split('/products/')[1].split('?')[0].split('#')[0];
          if (handle) break;
        }
      }

      if (handle) {
        products.push({ index: c, handle: handle, box: origBox });
      }
    }

    if (products.length === 0) return;

    // 2. Query Storefront API
    let queryAliases = products.map(function(p, i) {
      return 'p' + i + ': product(handle: "' + p.handle + '") { title priceRange { minVariantPrice { amount currencyCode } } variants(first: 1) { nodes { compareAtPrice { amount currencyCode } } } options { name values } modelYear: metafield(namespace: "custom", key: "model_year") { value } bikeCategory: metafield(namespace: "custom", key: "bike_category") { value } }';
    });
    let query = '{ ' + queryAliases.join('\n') + ' }';

    let productData = [];
    try {
      const res = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN },
        body: JSON.stringify({ query: query })
      });
      const json = await res.json();
      if (json.data) {
        for (var i = 0; i < products.length; i++) {
          productData.push(json.data['p' + i]);
        }
      }
    } catch (err) {
      console.warn('Failed fetching overview data:', err);
      return;
    }

    // fallback empty handling
    function valOrDash(val) { return (val && val.trim()) ? val : '—'; }

    // extract specific fields
    var rowData = { Model: [], Price: [], 'Bike Type': [], 'Model Year': [] };
    
    for (var i = 0; i < productData.length; i++) {
      var prod = productData[i];
      if (!prod) {
        rowData['Model'].push('—');
        rowData['Price'].push('—');
        rowData['Bike Type'].push('—');
        rowData['Model Year'].push('—');
        continue;
      }

      // Title
      rowData['Model'].push(valOrDash(prod.title));

      // Price
      var priceHTML = '—';
      if (prod.priceRange && prod.priceRange.minVariantPrice) {
        var currency = prod.priceRange.minVariantPrice.currencyCode;
        var amtFormatter = new Intl.NumberFormat('en-CA', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        var displayPrice = currency + ' ' + amtFormatter.format(prod.priceRange.minVariantPrice.amount);

        var comparePriceHTML = '';
        if (prod.variants && prod.variants.nodes && prod.variants.nodes.length > 0 && prod.variants.nodes[0].compareAtPrice) {
           var cmpAmt = prod.variants.nodes[0].compareAtPrice.amount;
           if (parseFloat(cmpAmt) > parseFloat(prod.priceRange.minVariantPrice.amount)) {
              var displayCompare = currency + ' ' + amtFormatter.format(cmpAmt);
              comparePriceHTML = '<span role="group"><span class="visually-hidden">Regular price </span><span class="compare-at-price">' + displayCompare + '</span></span>';
           }
        }

        if (comparePriceHTML) {
            priceHTML = '<div ref="priceContainer"><span role="group"><span class="visually-hidden">Sale price </span><span class="price">' + displayPrice + '</span></span> ' + comparePriceHTML + '</div>';
        } else {
            priceHTML = '<div ref="priceContainer"><span class="price">' + displayPrice + '</span></div>';
        }
      }
      rowData['Price'].push(priceHTML);

      // Bike Type
      var cat = prod.bikeCategory ? prod.bikeCategory.value : '';
      if(cat && cat.startsWith('[') && cat.endsWith(']')) {
         try { cat = JSON.parse(cat).join(', '); } catch { }
      }
      rowData['Bike Type'].push(valOrDash(cat));

      // Model Year
      var myYear = prod.modelYear ? prod.modelYear.value : '';
      rowData['Model Year'].push(valOrDash(myYear));
    }

    // 3. Build DOM
    var details = document.createElement('details');
    details.className = 'spec-accordion spec-overview-accordion';
    details.setAttribute('open', '');

    var summary = document.createElement('summary');
    summary.className = 'spec-accordion__header';
    var titleSpan = document.createElement('span');
    titleSpan.textContent = 'Overview';
    summary.appendChild(titleSpan);
    summary.appendChild(chevronSvg());
    details.appendChild(summary);

    var body = document.createElement('div');
    body.className = 'spec-accordion__body';
    body.style.setProperty('--product-count', String(products.length));

    var fields = ['Model', 'Price', 'Bike Type', 'Model Year'];
    for (var f = 0; f < fields.length; f++) {
      var field = fields[f];
      var subRow = document.createElement('div');
      subRow.className = 'spec-sub-row';
      var labelDiv = document.createElement('div');
      labelDiv.className = 'spec-sub-row__label';
      labelDiv.textContent = field;
      subRow.appendChild(labelDiv);

      var allVals = rowData[field];
      for (var p = 0; p < products.length; p++) {
        var valStr = allVals[p] || '—';
        var valDiv = document.createElement('div');
        valDiv.className = 'spec-sub-row__value';
        if (field === 'Price' && valStr !== '—') {
            valDiv.innerHTML = valStr;
        } else {
            valDiv.textContent = valStr;
        }
        subRow.appendChild(valDiv);
      }
      
      // highlight diff logic
      var allSame = true;
      var valsList = [];
      var diffNodes = subRow.querySelectorAll('.spec-sub-row__value');
      for(var z=0; z<diffNodes.length; z++) { valsList.push(diffNodes[z].textContent); }
      if (valsList.length > 0) {
          allSame = valsList.every(function(val) { return val === valsList[0]; });
          if (!allSame) {
            subRow.classList.add('spec-sub-row--diff');
          }
      }

      body.appendChild(subRow);
    }
    
    details.appendChild(body);

    // Insert Overview Accordion *before* the first Restructured row
    var firstAccordionRow = document.querySelector('.sp-comp-t-row[data-metaobject-restructured]');
    if (firstAccordionRow) {
       // We can wrap it in an Equate row element!
       var wrapRow = document.createElement('div');
       wrapRow.className = 'sp-comp-t-row sp-comp-t-row-meta sp-comp-t-row-overview sp-comp-t-row-disparate';
       wrapRow.dataset.metaobjectRestructured = 'true';
       wrapRow.appendChild(details);
       firstAccordionRow.parentNode.insertBefore(wrapRow, firstAccordionRow);
    }
    
    hideTopProductInfo();
  }

  function findElementsWithGids(root) {
    var elements = [];
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return node.textContent && GID_TEST.test(node.textContent)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    while (walker.nextNode()) {
      var el = walker.currentNode.parentElement;
      if (el && !el.dataset.metaobjectResolved) {
        elements.push(el);
      }
    }
    return elements.filter(function (v, i, a) { return a.indexOf(v) === i; });
  }

  async function processElements() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      var elements = findElementsWithGids(document.body);
      if (elements.length === 0) {
        // Even if no new GIDs, try restructuring already-resolved rows
        restructureRows();
        syncColumnWidth();
        await buildOverviewAccordion();
        return;
      }

      // Collect all GIDs
      var allGids = new Set();
      for (var e = 0; e < elements.length; e++) {
        var matches = elements[e].textContent.match(GID_PATTERN);
        if (matches) matches.forEach(function (gid) { allGids.add(gid); });
      }

      // Fetch from API
      await fetchMetaobjects([...allGids]);

      // Replace content
      for (var e = 0; e < elements.length; e++) {
        var el = elements[e];
        var matches = el.textContent.match(GID_PATTERN);
        if (!matches) continue;

        var uniqueGids = matches.filter(function (v, i, a) { return a.indexOf(v) === i; });
        var lines = formatMetaobjects(uniqueGids);
        if (lines.length === 0) continue;

        el.dataset.metaobjectResolved = 'true';

        el.innerHTML = '';
        var list = document.createElement('div');
        list.style.cssText = 'display:flex;flex-direction:column;gap:4px;';
        for (var l = 0; l < lines.length; l++) {
          var item = document.createElement('span');
          item.textContent = lines[l];
          list.appendChild(item);
        }
        el.appendChild(list);
      }

      // After all cells are resolved, restructure into accordions
      restructureRows();
      syncColumnWidth();
      await buildOverviewAccordion();
    } catch (err) {
      console.warn('[compare-resolver] Processing error:', err);
    } finally {
      isProcessing = false;
    }
  }

  function debouncedProcess() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(processElements, 500);
  }

  // Observe the DOM for the comparison app's late-loading content
  var observer = new MutationObserver(function (mutations) {
    var hasRelevantChange = false;
    for (var m = 0; m < mutations.length; m++) {
      var mutation = mutations[m];
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        for (var n = 0; n < mutation.addedNodes.length; n++) {
          var node = mutation.addedNodes[n];
          if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
            var text = node.textContent || '';
            if (GID_TEST.test(text)) {
              hasRelevantChange = true;
              break;
            }
          }
        }
      }
      if (hasRelevantChange) break;
    }

    if (hasRelevantChange) {
      debouncedProcess();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Also run once on load in case content is already present
  debouncedProcess();
})();
